import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { DatabaseIcon, HeartHandshakeIcon, TimerIcon, TagIcon, ShieldCheckIcon, TrendingUpIcon, CheckCircleIcon, StarIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
const advantageIcons = [<DatabaseIcon className="w-6 h-6" data-id="element-42" data-mp="d23378gejudt" />, <HeartHandshakeIcon className="w-6 h-6" data-id="element-43" data-mp="i3sacd0i7nwm" />, <TimerIcon className="w-6 h-6" data-id="element-44" data-mp="i30i9c6488b3" />, <TagIcon className="w-6 h-6" data-id="element-45" data-mp="m4m1rgd5nku9" />, <ShieldCheckIcon className="w-6 h-6" data-id="element-46" data-mp="m3st29umyl9h" />, <TrendingUpIcon className="w-6 h-6" data-id="element-47" data-mp="m2rhm49pjwr8" />];
const advantageStyles = [{
  iconBg: 'from-blue-100 to-blue-50',
  iconColor: 'text-blue-600',
  border: 'border-l-blue-400'
}, {
  iconBg: 'from-rose-100 to-rose-50',
  iconColor: 'text-rose-600',
  border: 'border-l-rose-400'
}, {
  iconBg: 'from-amber-100 to-amber-50',
  iconColor: 'text-amber-600',
  border: 'border-l-amber-400'
}, {
  iconBg: 'from-emerald-100 to-emerald-50',
  iconColor: 'text-emerald-600',
  border: 'border-l-emerald-400'
}, {
  iconBg: 'from-violet-100 to-violet-50',
  iconColor: 'text-violet-600',
  border: 'border-l-violet-400'
}, {
  iconBg: 'from-primary-100 to-primary-50',
  iconColor: 'text-primary-600',
  border: 'border-l-primary-400'
}];
export function AdvantagesSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="relative py-20 lg:py-28 bg-navy-50/50 overflow-hidden" data-id="element-48" data-mp="osvi01bp8ubp">
      <div className="absolute inset-0 pointer-events-none" data-id="element-49" data-mp="krs3bi5e14c2">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-primary-100/20 blur-3xl hidden sm:block" data-id="element-50" data-mp="kra8mpydiy69" />
        <div className="absolute bottom-0 left-1/4 w-[300px] h-[300px] rounded-full bg-cyan-100/15 blur-3xl hidden sm:block" data-id="element-51" data-mp="1hbiz75nkrp7i" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-52" data-mp="36qjuxblo1spv">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-53" data-mp="3bq6cfx8j1v4h">
          {/* Left: text */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-54" data-mp="2x1s81hvgpylt">
            <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-55" data-mp="2e25yv1e516gy">
              {t.advantages.eyebrow}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-56" data-mp="2j20ol1m6fq0f">
              {t.advantages.title}
            </h2>
            <p className="mt-4 text-lg text-navy-500 leading-relaxed" data-id="element-57" data-mp="hae4lk0pcvdg">
              {t.advantages.subtitle}
            </p>

            <div className="mt-10 space-y-4" data-id="element-58" data-mp="21sybsunomii7">
              <motion.div initial={{
              opacity: 0,
              y: 16
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="relative p-6 rounded-2xl bg-gradient-to-br from-primary-600 via-primary-700 to-navy-800 text-white overflow-hidden" data-id="element-59" data-mp="21pk91lb3w7a8">
                <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white/5 blur-2xl" data-id="element-60" data-mp="2phhkvcek2zbc" />
                <div className="absolute inset-0 opacity-[0.06]" style={{
                backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,1) 1px, transparent 0)`,
                backgroundSize: '24px 24px'
              }} data-id="element-61" data-mp="102jgji1d104z" />
                <div className="relative" data-id="element-62" data-mp="11j73mclhj4lj">
                  <div className="text-4xl font-extrabold tracking-tight" data-id="element-63" data-mp="11i5gh9r72rng">
                    98%
                  </div>
                  <div className="mt-1 text-primary-200 text-sm font-medium" data-id="element-64" data-mp="16gyur4eb2moo">
                    {t.advantages.stat1_label}
                  </div>
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 16
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="flex gap-4" data-id="element-65" data-mp="ds1gdcd7ta17">
                <div className="flex-1 p-5 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-66" data-mp="ehr5vsvpf9">
                  <div className="flex items-center gap-2 mb-1" data-id="element-67" data-mp="2daoa13p5hiry">
                    <StarIcon className="w-4 h-4 text-amber-500" data-id="element-68" data-mp="29bxbqt8y7v39" />
                    <span className="text-2xl font-extrabold text-navy-900" data-id="element-69" data-mp="24bjdg2x5fzif">
                      {t.advantages.stat2_val}
                    </span>
                  </div>
                  <div className="text-xs text-navy-500 font-medium" data-id="element-70" data-mp="1ue4xe4f51cqn">
                    {t.advantages.stat2_label}
                  </div>
                </div>
                <div className="flex-1 p-5 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-71" data-mp="1plv88qlukgpf">
                  <div className="flex items-center gap-2 mb-1" data-id="element-72" data-mp="1lm2ywbomq6j0">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-73" data-mp="1gnjdccdy6wr6" />
                    <span className="text-2xl font-extrabold text-navy-900" data-id="element-74" data-mp="1bndpz8epnwew">
                      {t.advantages.stat3_val}
                    </span>
                  </div>
                  <div className="text-xs text-navy-500 font-medium" data-id="element-75" data-mp="16qn9wl2wp0th">
                    {t.advantages.stat3_label}
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right: advantages list */}
          <div className="space-y-4" data-id="element-76" data-mp="o1l6m0dgwzsy">
            {t.advantages.items.map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: 30
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.1 + index * 0.1
          }} className={`flex gap-4 p-5 rounded-xl bg-white border border-navy-100 shadow-sm hover:shadow-md transition-shadow duration-300 border-l-4 ${advantageStyles[index].border}`} data-id="element-77" data-mp="s2n000kpehn0" data-mpi={index}>
                <div className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${advantageStyles[index].iconBg} ${advantageStyles[index].iconColor} flex items-center justify-center`} data-id="element-78" data-mp="3o64kyz42zn8u">
                  {advantageIcons[index]}
                </div>
                <div data-id="element-79" data-mp="3sz78api9m0gl">
                  <h3 className="font-bold text-navy-900" data-id="element-80" data-mp="3synwzrn4eabj">{item.title}</h3>
                  <p className="mt-1 text-sm text-navy-500 leading-relaxed" data-id="element-81" data-mp="359mk9s798d0g">
                    {item.description}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}